import { z } from 'zod';

// TF-006 (#112, A7/A8): Zuordnung pflegen — die drei Umhäng-Kanten teilen sich EINEN Rumpf.
//
//   A  kind: 'testcases' → ids sind Testfälle, target_id ein Prozess (test_cases.process_id)
//   B  kind: 'process'   → ids sind Prozesse,  target_id ein Teilprojekt-Knoten
//   C  kind: 'e2e'       → ids sind E2E-Ketten, target_id ein Stream-Knoten
//
// Ein gemeinsames Schema statt dreier, weil die Architektur genau EIN Bedienmuster für alle drei
// Kanten vorschreibt (auswählen → Ziel → Vorschau → ausführen → Teilerfolg). Drei Rümpfe hätten
// über kurz oder lang drei Dialoge nach sich gezogen — der Fehler, den #112 gerade auflöst.
//
// `target_id: null` ist kein Fehlerfall, sondern das Lösen der Zuordnung: Testfall → „Nicht
// zugeordnet", Prozess → „Ohne Teilprojekt", Kette → ohne Stream (A2 lässt Unzugeordnetes
// ausdrücklich zu).
export const ASSIGNMENT_KIND = ['testcases', 'process', 'e2e'] as const;
export type AssignmentKind = (typeof ASSIGNMENT_KIND)[number];

// #153: „Zu neuem Prozess zuordnen" — der Prozess entsteht in derselben Transaktion wie das
// Umhängen (services/assignment/apply.ts). Ein Zwei-Schritt im Frontend (erst Prozess anlegen, dann
// umhängen) ließe bei Abbruch einen leeren Prozess zurück. Das Teilprojekt ist Pflicht: „Ohne
// Teilprojekt" stellte genau das Problem wieder her, das die Funktion löst.
export const NewProcessSchema = z.object({
  name_de: z.string().trim().min(1).max(200),
  node_id: z.string().uuid(),
});
export type NewProcess = z.infer<typeof NewProcessSchema>;

// Obergrenze 1000 wie bei BulkAssignmentSchema (#91): großzügig über dem realen Katalog (546),
// aber ein Riegel gegen unbegrenzte Bodies.
export const AssignmentMoveSchema = z
  .object({
    kind: z.enum(ASSIGNMENT_KIND),
    ids: z.array(z.string().uuid()).min(1).max(1000),
    target_id: z.string().uuid().nullable(),
    new_process: NewProcessSchema.optional(),
  })
  .superRefine((m, ctx) => {
    if (!m.new_process) return;
    // Nur Testfälle wandern in einen Prozess; Prozesse/Ketten haben andere Ziele (Knoten).
    if (m.kind !== 'testcases') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['new_process'],
        message: 'new_process_only_for_testcases',
      });
    }
    // Ein vorhandenes Ziel UND ein neuer Prozess wären zwei widersprüchliche Ziele.
    if (m.target_id !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['new_process'],
        message: 'new_process_requires_null_target',
      });
    }
  });
export type AssignmentMove = z.infer<typeof AssignmentMoveSchema>;

/** Ein Knoten bzw. Prozess im Klartext — Quelle/Ziel der Vorschau. `id: null` = unzugeordnet. */
export interface AssignmentEndpoint {
  id: string | null;
  name: string | null;
}

/** Eine Bestätigung, die durch das Umhängen fallen würde (A8). */
export interface RevokedSignoffPreview {
  signoff_id: string;
  node_id: string;
  node_name: string;
  test_plan_id: string;
  plan_name: string;
}

export interface AssignmentFailure {
  id: string;
  reason: string;
}

/** Antwort von POST /assignment/preview — dieselbe Rechnung, die apply danach ausführt. */
export interface AssignmentPreview {
  /** Anzahl betroffener TESTFÄLLE (nicht der Eingabe-Objekte). */
  moved_count: number;
  /** Anzahl Objekte, die tatsächlich umgehängt würden. */
  moved_ids: string[];
  from: AssignmentEndpoint[];
  to: AssignmentEndpoint;
  signoffs_revoked: RevokedSignoffPreview[];
  failed: AssignmentFailure[];
}

/** Antwort von POST /assignment/apply — Teilerfolg-Muster aus #91. */
export interface AssignmentResult {
  ok: number;
  failed: AssignmentFailure[];
  revoked_count: number;
  /** #153: Id des in dieser Aktion angelegten Prozesses (nur bei `new_process` und mind. einem bewegten Testfall). */
  created_process_id?: string;
}
